import React, { useEffect, useState } from "react";
import api, { formatApiError, formatCurrency } from "../lib/api";
import { PageHeader, EmptyState } from "../components/Shared";
import { useProfession } from "../lib/professions";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, X, UploadSimple, BookOpen } from "@phosphor-icons/react";
import { ImportDialog } from "../components/ImportDialog";
import { LibraryDialog } from "../components/LibraryDialog";
import { MarginBadge } from "../components/MarginBadge";

const IMPORT_FIELDS = [
  { name: "name", label: "Nome", required: true, placeholder: "Restauração" },
  { name: "category", label: "Categoria", default: "Geral", placeholder: "Dentística" },
  { name: "duration_minutes", label: "Duração (min)", type: "number", required: true, default: 30, placeholder: "45" },
  { name: "professional", label: "Profissional", placeholder: "Dr. João" },
  { name: "materials", label: "Materiais (nome:qtd, ...)", placeholder: "Resina:1, Luvas:2" },
  { name: "lab_cost", label: "Laboratório (R$)", type: "number", default: 0, placeholder: "0" },
  { name: "extra_costs", label: "Outros custos (R$)", type: "number", default: 0, placeholder: "0" },
  { name: "current_price", label: "Preço atual (R$)", type: "number", default: 0, placeholder: "0" },
  { name: "description", label: "Observações", placeholder: "" },
];
const normName = (s) => String(s ?? "").trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

export default function ProceduresPage() {
  const prof = useProfession();
  const [items, setItems] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyForm = {
    name: "", category: "Geral", duration_minutes: 30,
    professional_id: "", extra_costs: 0, lab_cost: 0, current_price: "", description: "",
    materials: [],
  };
  const [form, setForm] = useState(emptyForm);
  const [importOpen, setImportOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);

  const transformImport = (row) => {
    const prof = professionals.find((p) => normName(p.name) === normName(row.professional) || (row.professional && normName(p.name).startsWith(normName(row.professional))));
    const mats = String(row.materials || "").split(/[,;]/).map((s) => s.trim()).filter(Boolean).map((s) => {
      const [n, q] = s.split(":").map((x) => x.trim());
      const mat = materials.find((m) => normName(m.name) === normName(n)) || materials.find((m) => normName(m.name).startsWith(normName(n)));
      return mat ? { material_id: mat.id, quantity: parseFloat(String(q || "1").replace(",", ".")) || 1 } : null;
    }).filter(Boolean);
    return {
      name: row.name, category: row.category || "Geral",
      duration_minutes: parseInt(row.duration_minutes || 0),
      professional_id: prof ? prof.id : null,
      materials: mats, extra_costs: parseFloat(row.extra_costs || 0), lab_cost: parseFloat(row.lab_cost || 0), current_price: parseFloat(row.current_price || 0) || null,
      description: row.description || "",
    };
  };

  const load = async () => {
    setLoading(true);
    try {
      const [p, m, pr] = await Promise.all([
        api.get("/procedures"),
        api.get("/materials"),
        api.get("/professionals"),
      ]);
      setItems(p.data);
      setMaterials(m.data);
      setProfessionals(pr.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name, category: item.category || "Geral",
      duration_minutes: item.duration_minutes,
      professional_id: item.professional_id || "",
      extra_costs: item.extra_costs || 0, lab_cost: item.lab_cost || 0, current_price: item.current_price ?? "",
      description: item.description || "",
      materials: item.materials || [],
    });
    setOpen(true);
  };

  const addMaterial = () => {
    setForm({ ...form, materials: [...form.materials, { material_id: "", quantity: 1 }] });
  };
  const updateMaterial = (idx, key, val) => {
    const list = [...form.materials];
    list[idx] = { ...list[idx], [key]: val };
    setForm({ ...form, materials: list });
  };
  const removeMaterial = (idx) => {
    setForm({ ...form, materials: form.materials.filter((_, i) => i !== idx) });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        duration_minutes: parseInt(form.duration_minutes || 0),
        extra_costs: parseFloat(form.extra_costs || 0), lab_cost: parseFloat(form.lab_cost || 0), current_price: form.current_price === "" ? null : parseFloat(form.current_price),
        professional_id: form.professional_id || null,
        materials: form.materials
          .filter((m) => m.material_id)
          .map((m) => ({ material_id: m.material_id, quantity: parseFloat(m.quantity || 0) })),
      };
      if (editing) {
        await api.put(`/procedures/${editing.id}`, payload);
        toast.success("Procedimento atualizado!");
      } else {
        await api.post("/procedures", payload);
        toast.success("Procedimento criado!");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir procedimento?")) return;
    try {
      await api.delete(`/procedures/${id}`);
      toast.success("Excluído");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="procedures-page">
      <PageHeader
        title={prof.procedures}
        subtitle={`Cadastre seus ${prof.procedures.toLowerCase()} e veja automaticamente o preço mínimo e o ideal (recomendado).`}
        action={
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setLibraryOpen(true)} data-testid="procedures-library">
              <BookOpen size={16} weight="bold" className="mr-1" /> Biblioteca
            </Button>
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="procedures-import">
              <UploadSimple size={16} weight="bold" className="mr-1" /> Importar lista
            </Button>
            <Button onClick={openNew} className="bg-indigo-600 hover:bg-indigo-700" data-testid="procedures-add">
              <Plus size={16} weight="bold" className="mr-1" /> Novo procedimento
            </Button>
          </div>
        }
      />

      <LibraryDialog open={libraryOpen} onOpenChange={setLibraryOpen} onDone={load} />
      <ImportDialog
        open={importOpen} onOpenChange={setImportOpen} title={prof.procedures} endpoint="procedures"
        fields={IMPORT_FIELDS} dataTestId="procedures" onDone={load} transform={transformImport}
        hint="Profissional e materiais são localizados pelo nome já cadastrado. Materiais: separe por vírgula e use dois-pontos para a quantidade (ex.: Resina:1, Luvas:2)."
      />

      {loading ? (
        <div className="text-slate-500">Carregando...</div>
      ) : items.length === 0 ? (
        <EmptyState
          title="Comece cadastrando um procedimento"
          description="O sistema calcula o preço com base nos seus custos, tempo e materiais."
          action={<Button onClick={openNew} className="bg-indigo-600 hover:bg-indigo-700">Criar procedimento</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {items.map((p) => (
            <div key={p.id} className="card-crisp p-5 flex flex-col">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-xs text-indigo-600 font-medium uppercase tracking-wider">{p.category}</div>
                  <h3 className="font-heading font-bold text-lg text-slate-900">{p.name}</h3>
                  <div className="text-sm text-slate-500 mt-0.5">{p.duration_minutes} min</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(p)} className="p-1.5 text-slate-500 hover:text-indigo-600" data-testid={`procedure-edit-${p.id}`}><PencilSimple size={16} /></button>
                  <button onClick={() => del(p.id)} className="p-1.5 text-slate-500 hover:text-rose-600" data-testid={`procedure-delete-${p.id}`}><Trash size={16} /></button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-auto pt-3 border-t border-slate-100">
                <PriceBox label="Mínimo" value={p.pricing.price_minimum} accent="rose" />
                <PriceBox label="Ideal (recomendado)" value={p.pricing.price_ideal} accent="emerald" />
              </div>
              <MarginBadge analysis={p.pricing.margin_analysis} priceIdeal={p.pricing.price_ideal} priceMinimum={p.pricing.price_minimum} testId={`procedure-margin-${p.id}`} />
              {p.pricing.prices_by_payment?.length > 1 && (
                <details className="mt-3 text-xs" data-testid={`procedure-payment-prices-${p.id}`}>
                  <summary className="cursor-pointer text-indigo-600 font-medium">Preço ideal por forma de pagamento · aproximado: {formatCurrency(p.pricing.price_approximate)}</summary>
                  <ul className="mt-2 space-y-1">
                    {p.pricing.prices_by_payment.map((f) => (
                      <li key={f.name} className="flex justify-between text-slate-600">
                        <span>{f.name} <span className="text-slate-400">({f.percentage}%)</span>{f.is_default ? " ★" : ""}</span>
                        <span className="font-mono-nums font-semibold text-slate-800">{formatCurrency(f.price_ideal)}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              <div className="text-xs text-slate-500 mt-3 font-mono-nums">
                Custo total: {formatCurrency(p.pricing.total_cost)}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar" : "Novo"} procedimento</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Nome do procedimento *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="procedure-field-name" className="mt-1" />
              </div>
              <div>
                <Label>Categoria</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="procedure-field-category" className="mt-1" />
              </div>
              <div>
                <Label>Duração (minutos) *</Label>
                <Input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} required data-testid="procedure-field-duration" className="mt-1" />
              </div>
              <div>
                <Label>Profissional</Label>
                <Select value={form.professional_id || "none"} onValueChange={(v) => setForm({ ...form, professional_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1" data-testid="procedure-field-professional"><SelectValue placeholder="Nenhum" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum</SelectItem>
                    {professionals.map((pr) => <SelectItem key={pr.id} value={pr.id}>{pr.name} - {pr.specialty}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Laboratório / terceiros (R$)</Label>
                <Input type="number" step="0.01" value={form.lab_cost} onChange={(e) => setForm({ ...form, lab_cost: e.target.value })} data-testid="procedure-field-lab" className="mt-1" placeholder="Prótese, escaneamento..." />
              </div>
              <div>
                <Label>Outros custos diretos (R$)</Label>
                <Input type="number" step="0.01" value={form.extra_costs} onChange={(e) => setForm({ ...form, extra_costs: e.target.value })} data-testid="procedure-field-extra" className="mt-1" />
              </div>
              <div>
                <Label>Quanto você cobra hoje? (R$)</Label>
                <Input type="number" step="0.01" value={form.current_price} onChange={(e) => setForm({ ...form, current_price: e.target.value })} data-testid="procedure-field-current" className="mt-1" placeholder="Opcional — para análise de margem" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Materiais utilizados</Label>
                <Button type="button" size="sm" variant="outline" onClick={addMaterial} data-testid="procedure-add-material">
                  <Plus size={14} className="mr-1" /> Adicionar
                </Button>
              </div>
              {form.materials.length === 0 && (
                <div className="text-sm text-slate-500 py-2">Nenhum material adicionado.</div>
              )}
              <div className="space-y-2">
                {form.materials.map((m, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <Select value={m.material_id || "pick"} onValueChange={(v) => updateMaterial(idx, "material_id", v === "pick" ? "" : v)}>
                      <SelectTrigger className="flex-1"><SelectValue placeholder="Selecione o material" /></SelectTrigger>
                      <SelectContent>
                        {materials.length === 0 && <SelectItem value="pick">Nenhum cadastrado</SelectItem>}
                        {materials.map((mat) => <SelectItem key={mat.id} value={mat.id}>{mat.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input type="number" step="0.01" value={m.quantity} onChange={(e) => updateMaterial(idx, "quantity", e.target.value)} className="w-28" placeholder="Qtd." />
                    <button type="button" onClick={() => removeMaterial(idx)} className="p-2 text-slate-500 hover:text-rose-600"><X size={16} /></button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Observações</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-1" />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="procedure-save">Salvar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PriceBox({ label, value, accent }) {
  const colors = {
    rose: "text-rose-700 bg-rose-50 border-rose-200",
    amber: "text-amber-700 bg-amber-50 border-amber-200",
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
  };
  return (
    <div className={`rounded-md border px-2 py-2 ${colors[accent]}`}>
      <div className="text-[10px] font-medium uppercase tracking-wider opacity-80">{label}</div>
      <div className="font-mono-nums font-bold text-sm">{formatCurrency(value)}</div>
    </div>
  );
}
